import { MapPinIcon, TagIcon } from "lucide-react";

import { Tag } from "@/web/components/primitives/tag";
import { getFirstPlacePhotoUrl } from "@/web/lib/googlePlaces/photoUtils";
import {
  PlaceFormatters,
  placeDisplayName,
  placePrimaryTypeDisplayName,
} from "@/web/lib/googlePlaces/placeFormatters";
import { cn } from "@/web/lib/styling";

function stripUrlScheme(url: string): string {
  return url.replace(/^https?:\/\//, "");
}

/**
 * Rich confirm card with Google photo + formatted fields (ploutos `PlaceOverview` equivalent).
 */
export function ConfirmPlaceOverview({
  place,
  websiteUrl,
  morePadding,
  showWebsiteUrl,
  className,
}: {
  place: google.maps.places.Place;
  /** Current website field (may differ from `place` while user edits). */
  websiteUrl: string;
  morePadding?: boolean;
  showWebsiteUrl?: boolean;
  className?: string;
}) {
  const photoSrc = getFirstPlacePhotoUrl(place);
  const typeLabel = placePrimaryTypeDisplayName(place);

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-2 border border-primary/10 bg-card p-3",
        morePadding && "p-5",
        className,
      )}
    >
      <div className={cn("flex items-center gap-2", morePadding ? "md:gap-7" : "md:gap-4")}>
        <div className="size-28 shrink-0 overflow-hidden bg-muted">
          {photoSrc ? (
            <img
              src={photoSrc}
              alt={placeDisplayName(place)}
              className="size-full object-cover transition-opacity duration-500"
            />
          ) : (
            <div className="flex size-full items-center justify-center text-center text-xs text-muted-foreground">
              No photo
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          <div className="withus-strong text-md line-clamp-2 whitespace-pre-wrap md:line-clamp-1 md:whitespace-normal">
            {placeDisplayName(place)}
          </div>
          <div className="-mt-2.5 flex flex-col gap-0.5">
            <div className="text-sm text-foreground/60">
              {PlaceFormatters.getStreetAddress(place)}
            </div>
            {showWebsiteUrl && (
              <div className="flex items-center gap-2">
                {websiteUrl ? (
                  <a
                    className="flex items-center gap-2 text-sm text-foreground/60"
                    href={websiteUrl.startsWith("http") ? websiteUrl : `https://${websiteUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="line-clamp-1">{stripUrlScheme(websiteUrl)}</span>
                  </a>
                ) : (
                  <div className="text-sm text-primary italic">Missing website</div>
                )}
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {PlaceFormatters.getCityState(place) && (
              <Tag icon={<MapPinIcon />}>{PlaceFormatters.getCityState(place)}</Tag>
            )}
            {typeLabel && <Tag icon={<TagIcon />}>{typeLabel}</Tag>}
          </div>
        </div>
      </div>
    </div>
  );
}
